import { useEffect } from "react";
import { Modal, Form, Select, InputNumber, Input, DatePicker, Row, Col, Divider } from "antd";
import dayjs from "dayjs";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import useRiderOptions from "../../hooks/useRiderOptions";

const { TextArea } = Input;

/** Components that add to Cash Payable. */
const EARNING_FIELDS = [
  { name: "orderEarnings", label: "Order Earnings" },
  { name: "incentivePay", label: "Incentive Pay" },
  { name: "extraIncentive", label: "Extra Incentive" },
  { name: "tips", label: "Tips" },
  { name: "benefits", label: "Benefits" },
  { name: "positiveAdjustment", label: "Positive Adjustment" },
];

/** Components that subtract from Cash Payable. */
const DEDUCTION_FIELDS = [
  { name: "deductions", label: "Deductions" },
  { name: "currentCodDeductions", label: "Current COD Deductions" },
  { name: "previousCodDeductions", label: "Previous COD Deductions" },
  { name: "advanceCodDeductions", label: "Advance COD Deductions" },
  { name: "ddc", label: "DDC" },
  { name: "dpc", label: "DPC" },
];

/**
 * Payout create/edit form.
 *
 * Cash Payable is deliberately not an input — the backend derives it from these
 * components on save, so an editable total could disagree with its own parts.
 */
export default function PayoutFormModal({ open, record, saving, onCancel, onSubmit }) {
  const [form] = Form.useForm();
  const { options: projectOptions } = useSettingsDropdown("projects");
  const { options: schemeOptions } = useSettingsDropdown("salary-schemes");
  const { options: riderOptions, loading: ridersLoading, onSearch: onRiderSearch } =
    useRiderOptions();

  useEffect(() => {
    if (!open) return;
    if (record) {
      form.setFieldsValue({
        rider: record.rider?._id,
        period: record.year ? dayjs(`${record.year}-${String(record.month).padStart(2, "0")}-01`) : null,
        project: record.project?._id,
        salaryScheme: record.salaryScheme?._id,
        completedOrders: record.completedOrders,
        ...Object.fromEntries(
          [...EARNING_FIELDS, ...DEDUCTION_FIELDS].map((f) => [f.name, record[f.name]])
        ),
        notes: record.notes,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ period: dayjs() });
    }
  }, [open, record, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    const { period, ...rest } = values;
    onSubmit({
      ...rest,
      month: period.month() + 1,
      year: period.year(),
    });
  };

  const moneyInput = (field) => (
    <Col span={12} key={field.name}>
      <Form.Item name={field.name} label={field.label}>
        <InputNumber style={{ width: "100%" }} precision={3} />
      </Form.Item>
    </Col>
  );

  return (
    <Modal
      open={open}
      title={record ? "Edit Payout" : "Add Payout"}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={saving}
      okText="Save"
      width={760}
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="rider" label="Partner" rules={[{ required: true }]}>
              <Select
                showSearch
                filterOption={false}
                onSearch={onRiderSearch}
                loading={ridersLoading}
                options={riderOptions}
                placeholder="Search by partner ID or name"
                disabled={Boolean(record)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="period" label="Payout Month" rules={[{ required: true }]}>
              <DatePicker
                picker="month"
                format="MM/YYYY"
                style={{ width: "100%" }}
                disabled={Boolean(record)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="project" label="Project">
              <Select options={projectOptions} allowClear />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="salaryScheme" label="Salary Scheme">
              <Select options={schemeOptions} allowClear />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="completedOrders" label="Completed Orders">
              <InputNumber style={{ width: "100%" }} min={0} precision={0} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>
          Earnings (OMR)
        </Divider>
        <Row gutter={16}>{EARNING_FIELDS.map(moneyInput)}</Row>

        <Divider orientation="left" plain>
          Deductions (OMR)
        </Divider>
        <Row gutter={16}>{DEDUCTION_FIELDS.map(moneyInput)}</Row>

        <Form.Item name="notes" label="Notes">
          <TextArea rows={2} maxLength={1000} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}
